/* Oral Coach — Speech-to-text + AI feedback.
   - FREE: 1 demo question with live GPT-4o correction.
   - PAID: real questions with precomputed model answers (no LLM cost).
   - Web Speech API for voice input (browser-native, no cost).
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Mic, MicOff, Send, Loader2, Sparkles, Trophy, Lock,
  CheckCircle2, AlertCircle, Volume2, RotateCw, ChevronRight,
  Flame, ArrowLeft, Award,
} from "lucide-react";
import { apiGet, apiPost } from "../lib/api";
import { buildFingerprintComponents } from "../lib/deviceFingerprint";

// ===== Strings (Italian forced for the simulator experience, ES mirror kept) =====
const STRINGS = {
  it: {
    eyebrow: "Coach Orale · Esame Italiano",
    title: "Esame Orale · Allenamento personale",
    subtitle:
      "Una domanda alla volta. Parli al microfono o scrive la risposta. Il valutatore le restituisce un feedback strutturato.",
    intro_free_eyebrow: "Versione introduttiva gratuita",
    intro_free_title: "Prova introduttiva — 1 domanda, valutazione IA in tempo reale",
    intro_free_body:
      "Una sola domanda demo per provare l'esperienza. Per esercitarsi sulle vere domande ministeriali con feedback completo, sblocchi il pacchetto premium.",
    intro_paid_title: "Allenamento orale personalizzato",
    intro_paid_body:
      "Vere domande dell'esame ministeriale, valutazione modello pre-controllata dai nostri docenti. Lei risponde, il sistema le mostra la risposta ideale + criteri di valutazione + clinical pearls.",
    cta_start: "Inizia l'allenamento",
    cta_start_free: "Prova gratuita (1 domanda)",
    cta_starting: "Preparo la domanda…",
    credits_remaining: "Tentativi rimanenti",
    no_credits_title: "Tentativi esauriti",
    no_credits_body: "Sblocchi un pacchetto per continuare ad allenarsi.",
    blocked_eyebrow: "Versione introduttiva già utilizzata",
    blocked_title: "Questo dispositivo ha già fatto la prova gratuita",
    blocked_body:
      "Per garantire l'integrità dell'allenamento, ogni dispositivo può fare la prova introduttiva una sola volta. Sblocchi un pacchetto premium per continuare.",
    question_label: "Domanda",
    answer_placeholder:
      "Scriva qui la sua risposta o usi il microfono per dettarla…",
    mic_start: "Attiva microfono",
    mic_stop: "Disattiva microfono",
    mic_listening: "Sto ascoltando…",
    mic_unsupported: "Il suo browser non supporta il riconoscimento vocale. Scriva la risposta.",
    submit: "Invia risposta",
    submitting: "Sto valutando…",
    submit_short: "Scriva almeno una frase prima di inviare.",
    feedback_eyebrow: "Valutazione",
    feedback_score: "Punteggio",
    feedback_correct: "Cosa ha fatto bene",
    feedback_missing: "Cosa è mancato",
    feedback_model: "Risposta modello",
    feedback_criteria: "Criteri di valutazione",
    feedback_pearls: "Clinical pearls",
    feedback_source_gpt: "Valutazione IA (GPT-4o)",
    feedback_source_pre: "Risposta modello dei docenti",
    feedback_source_fb: "Valutatore non disponibile — riprovi tra qualche istante",
    next_btn_paid: "Prossima domanda",
    next_btn_paid_loading: "Preparo la prossima…",
    next_btn_free_upsell: "🔥 Sblocca pacchetto premium",
    pack_5_title: "5 domande premium",
    pack_5_cta: "Sblocca 5 · €49,90",
    pack_10_title: "10 domande premium",
    pack_10_cta: "Sblocca 10 · €79,90",
    pack_10_recommended: "Consigliato",
    pack_unavailable: "Stiamo finalizzando i pacchetti, contattaci a info@esamericonoscimento.it",
    back: "Torna all'Academia",
    error_generic: "Qualcosa è andato storto. Riprovi tra qualche secondo.",
  },
  es: {
    eyebrow: "Coach Oral · Examen Italiano",
    title: "Examen Oral · Entrenamiento personal",
    subtitle:
      "Una pregunta a la vez. Habla al micrófono o escribe la respuesta. El evaluador te devuelve un feedback estructurado.",
    intro_free_eyebrow: "Versión introductoria gratuita",
    intro_free_title: "Prueba introductoria — 1 pregunta, evaluación IA en tiempo real",
    intro_free_body:
      "Una sola pregunta demo para probar la experiencia. Para entrenar sobre las preguntas ministeriales reales con feedback completo, desbloqueá el paquete premium.",
    intro_paid_title: "Entrenamiento oral personalizado",
    intro_paid_body:
      "Preguntas reales del examen ministerial, evaluación modelo pre-revisada por nuestros docentes. Vos respondés, el sistema te muestra la respuesta ideal + criterios + clinical pearls.",
    cta_start: "Iniciar entrenamiento",
    cta_start_free: "Prueba gratuita (1 pregunta)",
    cta_starting: "Preparo la pregunta…",
    credits_remaining: "Intentos restantes",
    no_credits_title: "Intentos agotados",
    no_credits_body: "Desbloqueá un paquete para seguir entrenando.",
    blocked_eyebrow: "Prueba introductoria ya utilizada",
    blocked_title: "Este dispositivo ya hizo la prueba gratuita",
    blocked_body:
      "Para preservar la integridad del entrenamiento, cada dispositivo puede hacer la prueba introductoria una sola vez. Desbloqueá un paquete premium para seguir.",
    question_label: "Pregunta",
    answer_placeholder:
      "Escribí tu respuesta o usá el micrófono para dictarla…",
    mic_start: "Activar micrófono",
    mic_stop: "Desactivar micrófono",
    mic_listening: "Escuchando…",
    mic_unsupported: "Tu navegador no soporta reconocimiento de voz. Escribí la respuesta.",
    submit: "Enviar respuesta",
    submitting: "Evaluando…",
    submit_short: "Escribí al menos una frase antes de enviar.",
    feedback_eyebrow: "Evaluación",
    feedback_score: "Puntaje",
    feedback_correct: "Qué hiciste bien",
    feedback_missing: "Qué faltó",
    feedback_model: "Respuesta modelo",
    feedback_criteria: "Criterios de evaluación",
    feedback_pearls: "Clinical pearls",
    feedback_source_gpt: "Evaluación IA (GPT-4o)",
    feedback_source_pre: "Respuesta modelo de los docentes",
    feedback_source_fb: "Evaluador no disponible — probá de nuevo en unos segundos",
    next_btn_paid: "Próxima pregunta",
    next_btn_paid_loading: "Preparo la próxima…",
    next_btn_free_upsell: "🔥 Desbloquear paquete premium",
    pack_5_title: "5 preguntas premium",
    pack_5_cta: "Desbloquear 5 · €49,90",
    pack_10_title: "10 preguntas premium",
    pack_10_cta: "Desbloquear 10 · €79,90",
    pack_10_recommended: "Recomendado",
    pack_unavailable: "Estamos finalizando los paquetes, escribinos a info@esamericonoscimento.it",
    back: "Volver a la Academia",
    error_generic: "Algo salió mal. Probá de nuevo en unos segundos.",
  },
};

const FADE_UP = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

// ===== Microphone hook (Web Speech API) =====
function useSpeechRecognition({ lang = "it-IT", onTranscript }) {
  const recognitionRef = useRef(null);
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSupported(true);
    const r = new SR();
    r.lang = lang;
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += transcript + " ";
        else interim += transcript;
      }
      if (final) onTranscript?.(final, true);
      else if (interim) onTranscript?.(interim, false);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recognitionRef.current = r;
    return () => { try { r.stop(); } catch { /* noop */ } };
  }, [lang, onTranscript]);

  const start = () => {
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.start();
      setListening(true);
    } catch { /* already started */ }
  };
  const stop = () => {
    if (!recognitionRef.current) return;
    try { recognitionRef.current.stop(); } catch { /* noop */ }
    setListening(false);
  };
  return { supported, listening, start, stop };
}

// ===== Page =====
export default function OralCoach() {
  // Oral coach UI is forced to Italian to match the real ministerial exam,
  // exactly like the written simulator.
  const lang = "it";
  const t = useMemo(() => STRINGS[lang] || STRINGS.it, []);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(null);
  const [packs, setPacks] = useState(null);
  const [activeAttempt, setActiveAttempt] = useState(null); // { attempt_id, question, mode }
  const [userAnswer, setUserAnswer] = useState("");
  const [interim, setInterim] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [deviceBlocked, setDeviceBlocked] = useState(false);
  const [error, setError] = useState(null);

  // Voice
  const handleTranscript = (text, isFinal) => {
    if (isFinal) {
      setUserAnswer((prev) => (prev ? prev + " " + text.trim() : text.trim()));
      setInterim("");
    } else {
      setInterim(text);
    }
  };
  const { supported: micSupported, listening, start: micStart, stop: micStop } =
    useSpeechRecognition({ lang: "it-IT", onTranscript: handleTranscript });

  // Load credits on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const c = await apiGet("/vip/oral-coach/credits");
        if (!alive) return;
        setCredits(c.credits || null);
        setPacks(c.packs || null);
      } catch (e) {
        setError(t.error_generic);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    setFeedback(null);
    setUserAnswer("");
    setInterim("");
    try {
      const fingerprint = buildFingerprintComponents();
      const res = await apiPost("/vip/oral-coach/start", { fingerprint });
      setActiveAttempt({
        attempt_id: res.attempt_id,
        question: res.question,
        mode: res.mode,
      });
      if (res.credits) setCredits(res.credits);
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      if (status === 402) {
        if (body.detail === "device_already_consumed_free_intro") {
          setDeviceBlocked(true);
        }
        if (body.credits) setCredits(body.credits);
        if (body.packs) setPacks(body.packs);
      } else {
        setError(body.detail || t.error_generic);
      }
    } finally {
      setStarting(false);
    }
  };

  const handleSubmit = async () => {
    const answer = (userAnswer + " " + interim).trim();
    if (answer.length < 5) {
      toast.error(t.submit_short);
      return;
    }
    if (listening) micStop();
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiPost("/vip/oral-coach/submit", {
        attempt_id: activeAttempt.attempt_id,
        user_answer: answer,
        language: lang,
      });
      setFeedback(res.evaluation);
      if (res.credits) setCredits(res.credits);
      if (res.packs) setPacks(res.packs);
    } catch (e) {
      setError(e?.response?.data?.detail || t.error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    setActiveAttempt(null);
    setFeedback(null);
    setUserAnswer("");
    setInterim("");
    await handleStart();
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (deviceBlocked) {
    return (
      <DeviceBlockedView t={t} packs={packs} onBack={() => navigate("/vip")} />
    );
  }

  // ---- Feedback / paywall after submission ----
  if (feedback && activeAttempt) {
    return (
      <FeedbackView
        t={t} feedback={feedback} attempt={activeAttempt}
        credits={credits} packs={packs}
        onNext={handleNext}
        onBack={() => navigate("/vip")}
      />
    );
  }

  // ---- Active question runner ----
  if (activeAttempt) {
    return (
      <RunnerView
        t={t} attempt={activeAttempt} userAnswer={userAnswer}
        interim={interim} setUserAnswer={setUserAnswer}
        micSupported={micSupported} listening={listening}
        micStart={micStart} micStop={micStop}
        submitting={submitting} onSubmit={handleSubmit}
        onBack={() => { if (listening) micStop(); setActiveAttempt(null); }}
      />
    );
  }

  // ---- Intro (credits-aware hero) ----
  return (
    <IntroView
      t={t} credits={credits} packs={packs}
      starting={starting} error={error}
      onStart={handleStart}
    />
  );
}

// ===== Sub-views =====

function IntroView({ t, credits, packs, starting, error, onStart }) {
  const isFreeMode = credits && credits.paid_left === 0 && credits.free_left > 0;
  const noCredits = credits && credits.total_left === 0;
  return (
    <motion.section data-testid="oral-coach-intro" variants={FADE_UP}
                    initial="hidden" animate="show"
                    className="max-w-3xl mx-auto py-8 sm:py-10">
      <p className="text-[11px] uppercase tracking-[0.22em] text-brand-secondary mb-3 inline-flex items-center gap-2">
        <Trophy className="h-3.5 w-3.5" /> {t.eyebrow}
      </p>
      <h1 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-tight mb-3">
        {isFreeMode ? t.intro_free_title : t.intro_paid_title}
      </h1>
      <p className="text-brand-secondary text-lg leading-relaxed mb-6">
        {isFreeMode ? t.intro_free_body : t.intro_paid_body}
      </p>

      {credits ? (
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-cream border border-brand-border text-sm mb-6"
             data-testid="oral-coach-credits-chip">
          <Award className="h-4 w-4 text-brand-secondary" />
          <span className="text-brand-primary">
            <strong>{credits.total_left}</strong> {t.credits_remaining.toLowerCase()}
          </span>
          <span className="text-brand-secondary text-xs">
            ({credits.free_left} free · {credits.paid_left} premium)
          </span>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 mb-4 inline-flex items-center gap-2 text-rose-800">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      ) : null}

      {noCredits ? (
        <PaywallPanel t={t} packs={packs} testId="oral-coach-paywall" />
      ) : (
        <button onClick={onStart} disabled={starting}
                data-testid="oral-coach-start-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-brand-primary text-white font-semibold shadow-premium-lg hover:bg-brand-green transition-colors w-full sm:w-auto disabled:opacity-50">
          {starting ? (
            <><Loader2 className="h-5 w-5 animate-spin" /> {t.cta_starting}</>
          ) : isFreeMode ? (
            <><Mic className="h-5 w-5" /> {t.cta_start_free}</>
          ) : (
            <><Mic className="h-5 w-5" /> {t.cta_start}</>
          )}
        </button>
      )}
    </motion.section>
  );
}

function RunnerView({ t, attempt, userAnswer, interim, setUserAnswer,
                     micSupported, listening, micStart, micStop,
                     submitting, onSubmit, onBack }) {
  const q = attempt.question;
  const isFree = attempt.mode === "free";
  return (
    <motion.section data-testid="oral-coach-runner" variants={FADE_UP}
                    initial="hidden" animate="show"
                    className="max-w-3xl mx-auto py-6">
      <button onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-brand-secondary hover:text-brand-primary mb-4">
        <ArrowLeft className="h-4 w-4" /> {t.back}
      </button>

      <div className="rounded-2xl border-2 overflow-hidden shadow-premium-lg"
           style={{ borderColor: "#B8862F" }}>
        {/* Italian tricolor bar */}
        <div className="flex h-1.5">
          <div className="flex-1" style={{ background: "#1A8A53" }} />
          <div className="flex-1" style={{ background: "#FFFFFF" }} />
          <div className="flex-1" style={{ background: "#C8102E" }} />
        </div>

        <div className="p-6 sm:p-8 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold"
                  style={{ color: "#9E1B32" }}>
              {t.question_label} · {q.topic}
            </span>
            {isFree && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold uppercase tracking-wider">
                Demo
              </span>
            )}
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-brand-primary leading-snug mb-6"
              data-testid="oral-coach-question-prompt">
            {q.prompt}
          </h2>

          <div className="relative">
            <textarea
              data-testid="oral-coach-answer-textarea"
              value={userAnswer + (interim ? " " + interim : "")}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder={t.answer_placeholder}
              rows={8}
              className="w-full rounded-xl border border-brand-border p-4 text-brand-primary text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-secondary/40 resize-y"
            />
            {listening && (
              <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
                {t.mic_listening}
              </div>
            )}
          </div>

          {!micSupported && (
            <p className="text-xs text-brand-secondary italic mt-2">
              {t.mic_unsupported}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-5">
            {micSupported && (
              <button
                onClick={listening ? micStop : micStart}
                data-testid="oral-coach-mic-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-medium transition-colors"
                style={{
                  borderColor: listening ? "#C8102E" : "#D4C8AC",
                  background: listening ? "#FEF2F2" : "white",
                  color: listening ? "#9E1B32" : "#3A2C5A",
                }}>
                {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                {listening ? t.mic_stop : t.mic_start}
              </button>
            )}
            <button
              onClick={onSubmit}
              disabled={submitting || !userAnswer.trim()}
              data-testid="oral-coach-submit-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-primary text-white font-semibold shadow-premium hover:bg-brand-green transition-colors disabled:opacity-50">
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> {t.submitting}</>
              ) : (
                <><Send className="h-4 w-4" /> {t.submit}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function FeedbackView({ t, feedback, attempt, credits, packs, onNext, onBack }) {
  const isFree = attempt.mode === "free";
  const isPrecomputed = feedback.source === "precomputed";
  const sourceLabel = isPrecomputed
    ? t.feedback_source_pre
    : feedback.source === "fallback"
    ? t.feedback_source_fb
    : t.feedback_source_gpt;
  const score = feedback.score;
  const scoreColor = score == null
    ? "#7a6e57"
    : score >= 8 ? "#1A8A53"
    : score >= 5 ? "#B8862F" : "#C8102E";
  const hasMoreCredits = (credits?.total_left || 0) > 0;

  return (
    <motion.section data-testid="oral-coach-feedback" variants={FADE_UP}
                    initial="hidden" animate="show"
                    className="max-w-3xl mx-auto py-6">
      <button onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-brand-secondary hover:text-brand-primary mb-4">
        <ArrowLeft className="h-4 w-4" /> {t.back}
      </button>

      <div className="rounded-2xl border bg-white p-6 sm:p-8 shadow-premium-lg"
           style={{ borderColor: "#D4C8AC" }}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold"
               style={{ color: "#9E1B32" }}>
              {t.feedback_eyebrow}
            </p>
            <h2 className="font-serif text-2xl text-brand-primary mt-1">
              {attempt.question.topic}
            </h2>
          </div>
          {score != null && (
            <div className="text-center" data-testid="oral-coach-feedback-score">
              <div className="text-3xl font-bold font-serif" style={{ color: scoreColor }}>
                {score}/10
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-secondary">
                {t.feedback_score}
              </div>
            </div>
          )}
        </div>

        <p className="text-xs italic text-brand-secondary mb-5"
           data-testid="oral-coach-feedback-source">
          <Sparkles className="inline h-3 w-3 mr-1" /> {sourceLabel}
        </p>

        {feedback.what_correct && feedback.what_correct.length > 0 && (
          <FeedbackSection title={t.feedback_correct} icon={CheckCircle2}
                           items={feedback.what_correct} accent="#1A8A53"
                           testId="oral-coach-feedback-correct" />
        )}
        {feedback.what_missing && feedback.what_missing.length > 0 && (
          <FeedbackSection title={t.feedback_missing} icon={AlertCircle}
                           items={feedback.what_missing} accent="#C8102E"
                           testId="oral-coach-feedback-missing" />
        )}

        <div className="rounded-xl p-4 mb-4"
             style={{ background: "rgba(184,134,47,0.08)", borderLeft: "3px solid #B8862F" }}
             data-testid="oral-coach-feedback-model">
          <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-1.5"
             style={{ color: "#7a5b13" }}>
            {t.feedback_model}
          </p>
          <p className="text-brand-primary leading-relaxed">{feedback.model_answer}</p>
        </div>

        {feedback.criteria && feedback.criteria.length > 0 && (
          <FeedbackSection title={t.feedback_criteria} icon={Trophy}
                           items={feedback.criteria} accent="#3A2C5A"
                           testId="oral-coach-feedback-criteria" />
        )}
        {feedback.clinical_pearls && feedback.clinical_pearls.length > 0 && (
          <FeedbackSection title={t.feedback_pearls} icon={Sparkles}
                           items={feedback.clinical_pearls} accent="#9E1B32"
                           testId="oral-coach-feedback-pearls" />
        )}

        <div className="mt-6 pt-5 border-t flex flex-wrap items-center gap-3"
             style={{ borderColor: "#D4C8AC" }}>
          {hasMoreCredits ? (
            <button onClick={onNext}
                    data-testid="oral-coach-next-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-primary text-white font-semibold shadow-premium hover:bg-brand-green transition-colors">
              <ChevronRight className="h-4 w-4" /> {t.next_btn_paid}
            </button>
          ) : (
            <PaywallPanel t={t} packs={packs} testId="oral-coach-paywall-after" />
          )}
        </div>
      </div>
    </motion.section>
  );
}

function FeedbackSection({ title, icon: Icon, items, accent, testId }) {
  return (
    <div className="mb-4" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2 inline-flex items-center gap-1.5"
         style={{ color: accent }}>
        <Icon className="h-3 w-3" /> {title}
      </p>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-brand-primary leading-relaxed">
            <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ background: accent }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PaywallPanel({ t, packs, testId }) {
  if (!packs) {
    return (
      <p className="text-sm italic text-brand-secondary" data-testid={testId}>
        {t.pack_unavailable}
      </p>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3 w-full" data-testid={testId}>
      <PackCard pack={packs.oral_coach_pack_5} title={t.pack_5_title}
                cta={t.pack_5_cta} unavailable_msg={t.pack_unavailable}
                testId="oral-coach-pack-5" />
      <PackCard pack={packs.oral_coach_pack_10} title={t.pack_10_title}
                cta={t.pack_10_cta} unavailable_msg={t.pack_unavailable}
                recommended_label={t.pack_10_recommended}
                testId="oral-coach-pack-10" />
    </div>
  );
}

function PackCard({ pack, title, cta, unavailable_msg, recommended_label, testId }) {
  if (!pack) return null;
  const hasUrl = !!pack.url;
  const recommended = !!pack.recommended;
  return (
    <a href={hasUrl ? pack.url : "#"}
       onClick={(e) => { if (!hasUrl) { e.preventDefault(); toast.info(unavailable_msg); } }}
       data-testid={testId}
       target={hasUrl ? "_blank" : undefined}
       rel="noopener noreferrer"
       className="rounded-xl p-4 border-2 transition-transform hover:scale-[1.02] block"
       style={{
         borderColor: recommended ? "#B8862F" : "#D4C8AC",
         background: recommended ? "linear-gradient(135deg,#FBF7EE 0%,#F8EBD0 100%)" : "white",
       }}>
      {recommended_label && recommended && (
        <span className="inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider mb-2"
              style={{ background: "#B8862F", color: "white" }}>
          {recommended_label}
        </span>
      )}
      <p className="font-serif text-lg text-brand-primary">{title}</p>
      <p className="text-xs text-brand-secondary mt-1">{pack.label_it}</p>
      <p className="text-sm font-semibold mt-3" style={{ color: "#9E1B32" }}>
        {cta} →
      </p>
    </a>
  );
}

function DeviceBlockedView({ t, packs, onBack }) {
  return (
    <motion.section data-testid="oral-coach-device-blocked"
                    variants={FADE_UP} initial="hidden" animate="show"
                    className="max-w-3xl mx-auto py-8">
      <button onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-brand-secondary hover:text-brand-primary mb-4">
        <ArrowLeft className="h-4 w-4" /> {t.back}
      </button>
      <div className="rounded-2xl border-2 p-6 sm:p-8"
           style={{
             borderColor: "rgba(184,134,47,0.6)",
             background: "linear-gradient(180deg,#FFFFFF 0%,#FBF7EE 100%)",
           }}>
        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-2"
           style={{ color: "#9E1B32" }}>
          <Lock className="inline h-3 w-3 mr-1" /> {t.blocked_eyebrow}
        </p>
        <h1 className="font-serif text-3xl text-brand-primary mb-2">
          {t.blocked_title}
        </h1>
        <p className="text-brand-secondary leading-relaxed mb-6">
          {t.blocked_body}
        </p>
        <PaywallPanel t={t} packs={packs} testId="oral-coach-blocked-paywall" />
      </div>
    </motion.section>
  );
}
